import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  formatMeasure,
  INDICATOR_OUTCOME_LABELS,
  INDICATOR_OUTCOMES,
  type IndicatorOutcome,
  type IndicatorRequest,
  type IndicatorResponse,
  indicatorSchema,
  type JobResponse,
  type MeasureRequest,
  measureSchema,
  QUALITY_INDICATORS,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { jobKeys, Meter } from './jobs-ui.js';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The objective of a progetto and its objective indicators (2.41): from a
 * start value to a target, measured over time; the outcome at the closing. A
 * commessa has them too since 2.46, for quality and the client, without the
 * objective; the proposed ones are added with one click.
 */
export function Indicators({ job }: { job: JobResponse }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<IndicatorResponse | 'new' | null>(null);
  const [measuring, setMeasuring] = useState<IndicatorResponse | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const edit = job.you.edit;
  const project = job.kind === 'progetto';
  const propose = useMutation({
    mutationFn: async () => {
      for (const indicator of QUALITY_INDICATORS) {
        await apiRequest<JobResponse>(`/jobs/${job.id}/indicators`, {
          method: 'POST',
          body: { ...indicator, dueOn: job.endOn },
        });
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: jobKeys.all }),
  });
  return (
    <section className="card stack">
      {project && (
        <>
          <h3>Obiettivo</h3>
          {job.objective ? (
            <p>{job.objective}</p>
          ) : (
            <p className="muted">Nessun obiettivo scritto: si indica con «Modifica».</p>
          )}
        </>
      )}
      <div className="row between">
        <h3>
          {project ? 'Indicatori' : 'Qualità e cliente'}{' '}
          <span className="count-pill">{job.indicators.length}</span>
        </h3>
        {edit && (
          <div className="row">
            {!project && job.indicators.length === 0 && (
              <button type="button" onClick={() => propose.mutate()} disabled={propose.isPending}>
                {propose.isPending ? 'Attendere…' : 'Aggiungi gli indicatori proposti'}
              </button>
            )}
            <button type="button" className="primary" onClick={() => setEditing('new')}>
              Aggiungi indicatore
            </button>
          </div>
        )}
      </div>
      {propose.isError && <p className="error">{propose.error.message}</p>}
      {!project && job.indicators.length === 0 && (
        <p className="muted small">
          Proposti: {QUALITY_INDICATORS.map((indicator) => `«${indicator.name}»`).join(', ')}. Poi
          si cambiano o si tolgono come gli altri.
        </p>
      )}
      {job.indicators.length === 0 ? (
        <p className="muted">
          Nessun indicatore: ognuno dice come si misura, da che valore si parte e quale si vuole
          raggiungere.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Indicatore</th>
                <th>Come si misura</th>
                <th className="right">Partenza</th>
                <th className="right">Obiettivo</th>
                <th>Entro</th>
                <th className="right">Ultima misura</th>
                <th>Avanzamento</th>
                <th>Esito</th>
                {edit && <th />}
              </tr>
            </thead>
            <tbody>
              {job.indicators.map((indicator) => {
                const last = indicator.measures[0];
                return (
                  <IndicatorRows
                    key={indicator.id}
                    job={job}
                    indicator={indicator}
                    last={last}
                    open={open === indicator.id}
                    onToggle={() => setOpen(open === indicator.id ? null : indicator.id)}
                    onEdit={() => setEditing(indicator)}
                    onMeasure={() => setMeasuring(indicator)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        Ogni misura resta nello storico con data, nota e chi l’ha inserita. Alla chiusura{' '}
        {project ? 'del progetto' : 'della commessa'} si registra l’esito di ogni indicatore.
      </p>
      {editing && (
        <IndicatorDialog
          job={job}
          indicator={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {measuring && (
        <MeasureDialog job={job} indicator={measuring} onClose={() => setMeasuring(null)} />
      )}
    </section>
  );
}

function IndicatorRows({
  job,
  indicator,
  last,
  open,
  onToggle,
  onEdit,
  onMeasure,
}: {
  job: JobResponse;
  indicator: IndicatorResponse;
  last: IndicatorResponse['measures'][number] | undefined;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onMeasure: () => void;
}) {
  const queryClient = useQueryClient();
  const edit = job.you.edit;
  const removeMeasure = useMutation({
    mutationFn: (measureId: string) =>
      apiRequest<JobResponse>(`/jobs/${job.id}/indicators/${indicator.id}/measures/${measureId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: jobKeys.all }),
  });
  return (
    <>
      <tr>
        <td>
          <button type="button" className="link" onClick={onToggle} aria-expanded={open}>
            <strong>{indicator.name}</strong>
          </button>
        </td>
        <td>{indicator.method ?? '—'}</td>
        <td className="right">{formatMeasure(indicator.baseline)}</td>
        <td className="right">{formatMeasure(indicator.target)}</td>
        <td>{indicator.dueOn ? formatDate(indicator.dueOn) : '—'}</td>
        <td className="right">
          {last ? (
            <>
              {formatMeasure(last.value)}{' '}
              <small className="muted">il {formatDate(last.measuredOn)}</small>
            </>
          ) : (
            '—'
          )}
        </td>
        <td>
          {indicator.progress === null ? (
            <span className="pill muted">da misurare</span>
          ) : (
            <>
              <Meter share={indicator.progress} />{' '}
              <small className="muted">{Math.round(indicator.progress * 100)}%</small>
            </>
          )}
        </td>
        <td>
          {indicator.outcome ? (
            <span
              className={`pill ${indicator.outcome === 'raggiunto' ? 'ok' : indicator.outcome === 'parziale' ? 'warn' : 'bad'}`}
            >
              {INDICATOR_OUTCOME_LABELS[indicator.outcome]}
            </span>
          ) : (
            '—'
          )}
        </td>
        {edit && (
          <td className="right nowrap">
            <button type="button" className="link" onClick={onMeasure}>
              Nuova misura…
            </button>{' '}
            <button type="button" className="link" onClick={onEdit}>
              Modifica
            </button>
          </td>
        )}
      </tr>
      {open && (
        <tr>
          <td colSpan={edit ? 9 : 8}>
            {indicator.measures.length === 0 ? (
              <span className="muted">Nessuna misura.</span>
            ) : (
              <ul className="plain-list">
                {indicator.measures.map((measure) => (
                  <li key={measure.id} className="row between">
                    <span>
                      {formatDate(measure.measuredOn)} ·{' '}
                      <strong>{formatMeasure(measure.value)}</strong>
                      {measure.note && <span className="muted"> · {measure.note}</span>}
                      {measure.createdBy && (
                        <small className="muted"> · da {measure.createdBy}</small>
                      )}
                    </span>
                    {edit && (
                      <button
                        type="button"
                        className="link"
                        disabled={removeMeasure.isPending}
                        onClick={() => removeMeasure.mutate(measure.id)}
                      >
                        Togli
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** A new indicator, or the change of one with its outcome. */
function IndicatorDialog({
  job,
  indicator,
  onClose,
}: {
  job: JobResponse;
  indicator: IndicatorResponse | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [removing, setRemoving] = useState(false);
  const [form, setForm] = useState({
    name: indicator?.name ?? '',
    method: indicator?.method ?? '',
    baseline: indicator ? formatMeasure(indicator.baseline).replace(/\./g, '') : '',
    target: indicator ? formatMeasure(indicator.target).replace(/\./g, '') : '',
    dueOn: indicator?.dueOn ?? '',
    outcome: (indicator?.outcome ?? '') as IndicatorOutcome | '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const text = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm({ ...form, [key]: event.target.value }),
  });
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: jobKeys.all });
    onClose();
  };
  const save = useMutation({
    mutationFn: (body: IndicatorRequest) =>
      indicator
        ? apiRequest<JobResponse>(`/jobs/${job.id}/indicators/${indicator.id}`, {
            method: 'PUT',
            body,
          })
        : apiRequest<JobResponse>(`/jobs/${job.id}/indicators`, { method: 'POST', body }),
    onError: (error) => showServerError(error, setErrors, setFormError),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: () =>
      apiRequest<JobResponse>(`/jobs/${job.id}/indicators/${indicator!.id}`, {
        method: 'DELETE',
      }),
    onSuccess: done,
  });
  if (removing && indicator) {
    return (
      <ConfirmDialog
        title={`Togliere «${indicator.name}»?`}
        confirmLabel="Togli l’indicatore"
        busy={remove.isPending}
        error={remove.isError ? remove.error.message : null}
        onConfirm={() => remove.mutate()}
        onCancel={() => setRemoving(false)}
      >
        <p>Se ne vanno anche le sue misure; resta la traccia nel registro attività.</p>
      </ConfirmDialog>
    );
  }
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>{indicator ? indicator.name : 'Nuovo indicatore'}</h2>
        <Field label="Indicatore" error={errors.name}>
          <input {...text('name')} maxLength={200} placeholder="Richieste di contatto al mese" />
        </Field>
        <Field
          label="Come si misura"
          error={errors.method}
          hint="La fonte o il modo: rende l’indicatore oggettivo."
        >
          <input {...text('method')} maxLength={300} placeholder="modulo contatti → CRM" />
        </Field>
        <div className="form-grid">
          <Field label="Valore di partenza" error={errors.baseline}>
            <input inputMode="decimal" {...text('baseline')} />
          </Field>
          <Field
            label="Obiettivo"
            error={errors.target}
            hint="Anche più basso della partenza, per esempio dei tempi."
          >
            <input inputMode="decimal" {...text('target')} />
          </Field>
          <Field label="Entro" error={errors.dueOn}>
            <input type="date" {...text('dueOn')} />
          </Field>
          {indicator && (
            <Field label="Esito" error={errors.outcome} hint="Si indica alla chiusura.">
              <select {...text('outcome')}>
                <option value="">—</option>
                {INDICATOR_OUTCOMES.map((outcome) => (
                  <option key={outcome} value={outcome}>
                    {INDICATOR_OUTCOME_LABELS[outcome]}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          {indicator && (
            <button type="button" className="link" onClick={() => setRemoving(true)}>
              Togli l’indicatore
            </button>
          )}
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !form.name.trim()}
            onClick={() => {
              const body: IndicatorRequest = {
                name: form.name,
                method: form.method,
                baseline: form.baseline,
                target: form.target,
                dueOn: form.dueOn || null,
                outcome: form.outcome || null,
              };
              const checked = validate(indicatorSchema, body);
              setErrors(checked.errors);
              setFormError(
                Object.keys(checked.errors).length ? 'Correggi i campi segnati in rosso.' : '',
              );
              if (!Object.keys(checked.errors).length) save.mutate(body);
            }}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** A measure of an indicator: the day, the value, a note. */
function MeasureDialog({
  job,
  indicator,
  onClose,
}: {
  job: JobResponse;
  indicator: IndicatorResponse;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState({ measuredOn: today(), value: '', note: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const text = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm({ ...form, [key]: event.target.value }),
  });
  const save = useMutation({
    mutationFn: (body: MeasureRequest) =>
      apiRequest<JobResponse>(`/jobs/${job.id}/indicators/${indicator.id}/measures`, {
        method: 'POST',
        body,
      }),
    onError: (error) => showServerError(error, setErrors, setFormError),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      onClose();
    },
  });
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Nuova misura · {indicator.name}</h2>
        <p className="muted small">
          Da {formatMeasure(indicator.baseline)} a {formatMeasure(indicator.target)}
          {indicator.method && ` · ${indicator.method}`}
        </p>
        <div className="form-grid">
          <Field label="Data" error={errors.measuredOn}>
            <input type="date" {...text('measuredOn')} />
          </Field>
          <Field label="Valore" error={errors.value}>
            <input inputMode="decimal" autoFocus {...text('value')} />
          </Field>
        </div>
        <Field label="Nota" error={errors.note}>
          <input {...text('note')} maxLength={300} />
        </Field>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !form.value.trim()}
            onClick={() => {
              const body: MeasureRequest = form;
              const checked = validate(measureSchema, body);
              setErrors(checked.errors);
              setFormError(
                Object.keys(checked.errors).length ? 'Correggi i campi segnati in rosso.' : '',
              );
              if (!Object.keys(checked.errors).length) save.mutate(body);
            }}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
